import { Message, useNotifications } from '@doveit/bricks';
import React from 'react';
import useJobPosition from '../../../job-position/hooks/use-job-position/useJobPosition';
import { JobApplication } from '../../../providers/api/dtos';
import { updateJobApplication } from '../../../providers/api/job-application/jobApplicationProvider';
import JobApplicationForm, { JobApplicationFormProps } from '../../components/job-application-form/JobApplicationForm';
import { jobApplicationFormModelToJobApplication, jobApplicationToJobApplicationFormModel } from '../../utils/mappers';
import { jobApplicationFormSchema } from '../../components/job-application-form/JobApplicationForm.schema';
import { JobApplicationFormModel } from '../../types';

export interface UpdateJobApplicationProps extends Pick<JobApplicationFormProps, 'innerRef' | 'hiddenFields' | 'disabled'> {
  jobApplication: JobApplication,
  onSuccess?: (jobApplication: JobApplication) => void
}

export const JOB_POSITION_LOAD_ERROR_MESSAGE = 'Non è stato possibile caricare il ruolo.';
export const JOB_APPLICATION_UPDATE_SUCCESS_MESSAGE = 'La candidatura è stata aggiornata con successo.';
export const JOB_APPLICATION_UPDATE_ERROR_MESSAGE = 'Non è stato possibile aggiornare la candidatura.';

const UpdateJobApplication: React.FC<UpdateJobApplicationProps> = ({
  jobApplication,
  innerRef,
  onSuccess,
}) => {
  const [isSaving, setIsSaving] = React.useState(false);
  const { data: jobPosition, error: jobPositionError } = useJobPosition(jobApplication?.jobPositionId);
  const { addSuccess, addError } = useNotifications();

  const initialValues: JobApplicationFormModel = React.useMemo(
    () => jobApplicationToJobApplicationFormModel(jobApplication, jobPosition!),
    [jobApplication, jobPosition],
  );

  const onSubmit = React.useCallback(async (values: JobApplicationFormModel) => {
    setIsSaving(true);
    try {
      const mergedJobApplication: JobApplication = jobApplicationFormModelToJobApplication(jobApplication, values);
      const savedJobApplication = await updateJobApplication(mergedJobApplication.id!, mergedJobApplication);
      setIsSaving(false);
      addSuccess(JOB_APPLICATION_UPDATE_SUCCESS_MESSAGE);
      onSuccess?.(savedJobApplication);
    } catch {
      setIsSaving(false);
      addError(JOB_APPLICATION_UPDATE_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, jobApplication, onSuccess]);

  if (jobPositionError) {
    return (
      <Message
        type="critical"
        message={JOB_POSITION_LOAD_ERROR_MESSAGE}
      />
    );
  }

  return (
    <JobApplicationForm
      innerRef={innerRef}
      initialValues={initialValues}
      loading={isSaving}
      validationSchema={jobApplicationFormSchema}
      onSubmit={onSubmit}
      disabledFields={{
        source: !!jobApplication.originUrl,
      }}
    />
  );
};

export default UpdateJobApplication;
